import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './app'
import { setDynamicSettings, HOSTED_APP } from './config/settings'
import {
  loadHostedAppSession,
  renewHostedSession,
  renewSessionOnChunkLoadFailure,
  setHostedUserToken,
  setHostedViewer,
} from './modules/auth/hosted-app'
import { useTokenStore } from './modules/auth/token-store'
import { ErrorBoundary } from './modules/errors/error-boundary'
import { ErrorFallback } from './modules/errors/fallback'
import { installGlobalErrorHandlers } from './modules/errors/global-handlers'
import { reportError } from './modules/errors/report'

// Before the boot await, so a failure while loading the session is reported too.
installGlobalErrorHandlers()

const root = createRoot(document.getElementById('root')!)

try {
  await initDynamicSettings()
  // The boundary sits OUTSIDE <App/>, i.e. above the providers (theme, auth, query), so it also
  // covers a failure while mounting them, which used to leave a blank screen. It can afford to,
  // because its fallback depends on no provider at all (see fallback.tsx).
  root.render(
    <StrictMode>
      <ErrorBoundary scope='app'>
        <App />
      </ErrorBoundary>
    </StrictMode>,
  )
} catch (error) {
  // Only the hosted boot can throw here (carto-info.json failing, or a renewal that came back to
  // a dead session). Without this the top-level await would reject before any boundary exists and
  // leave a blank page. The fallback needs no provider, and its retry signs in again.
  reportError('app', error, { url: window.location.href })
  root.render(
    <ErrorFallback
      error={error instanceof Error ? error : new Error(String(error))}
      onReset={renewHostedSession}
    />,
  )
}

async function initDynamicSettings() {
  if (HOSTED_APP) {
    // CARTO serves a per-viewer carto-info.json next to the bundle. Its token is the viewer's own
    // CARTO user JWT, so it goes through the USER token path (roles keep resolving per viewer, see
    // auth/hosted-app.ts) and `ready` is flipped here, the job the Auth0 provider does in login
    // mode and the static token does at module load in public mode. No fallback to the standard
    // flow: the hosted build only runs on the platform (or serve-hosted-local), and without a
    // viewer token it must not render rather than half-work. The API host is not overridden: the
    // Consorcio organization lives in gcp-us-east1, the same host the env file already sets.
    const { accessToken, viewer } = await loadHostedAppSession()
    setHostedUserToken(accessToken)
    setHostedViewer(viewer)
    renewSessionOnChunkLoadFailure()
    useTokenStore.getState().setReady(true)
    return
  }

  const searchParams = new URLSearchParams(window.location.search)
  setDynamicSettings({ accessToken: searchParams.get('accessToken') })
}
